import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogOut, Users as UsersIcon, MessageSquare, Wallet, TrendingUp, CheckCircle2 } from "lucide-react";
import api from "../lib/api";

const ADMIN_TOKEN_KEY = "gges_admin_token";

function saveAdmin(token) { localStorage.setItem(ADMIN_TOKEN_KEY, token); }
function clearAdmin() { localStorage.removeItem(ADMIN_TOKEN_KEY); }
function getAdminToken() { return localStorage.getItem(ADMIN_TOKEN_KEY); }

async function adminFetch(url) {
  const token = getAdminToken();
  return api.get(url, { headers: { Authorization: `Bearer ${token}` } });
}
async function adminPost(url) {
  const token = getAdminToken();
  return api.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
}

export default function Admin() {
  const nav = useNavigate();
  const [loggedIn, setLoggedIn] = useState(!!getAdminToken());
  const [tab, setTab] = useState("overview");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [payments, setPayments] = useState([]);
  const [referrals, setReferrals] = useState([]);

  useEffect(() => {
    if (!loggedIn) return;
    Promise.all([
      adminFetch("/admin/stats"),
      adminFetch("/admin/users"),
      adminFetch("/admin/tickets"),
      adminFetch("/admin/payments"),
      adminFetch("/admin/referrals"),
    ])
      .then(([s, u, t, p, r]) => {
        setStats(s.data);
        setUsers(u.data.users || []);
        setTickets(t.data.tickets || []);
        setPayments(p.data.payments || []);
        setReferrals(r.data.referrals || []);
      })
      .catch(() => {
        clearAdmin();
        setLoggedIn(false);
      });
  }, [loggedIn]);

  const login = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/admin/login", { email, password });
      saveAdmin(r.data.token);
      setLoggedIn(true);
      toast.success("Welcome back, admin");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAdmin();
    setLoggedIn(false);
    nav("/admin", { replace: true });
  };

  const resolve = async (id) => {
    try {
      await adminPost(`/admin/tickets/${id}/resolve`);
      setTickets((t) => t.map((x) => (x.id === id ? { ...x, status: "resolved" } : x)));
      toast.success("Ticket resolved");
    } catch (err) {
      toast.error("Failed");
    }
  };

  if (!loggedIn) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <form onSubmit={login} className="w-full max-w-sm bg-white rounded-3xl border border-neutral-200 p-6 shadow-sm">
          <div className="text-center">
            <div className="text-[10px] font-bold uppercase tracking-widest text-brand">Admin</div>
            <h1 className="font-display font-bold text-[24px] mt-1">GGES Ventures Console</h1>
          </div>
          <div className="mt-6 space-y-3">
            <input
              data-testid="admin-email-input"
              type="email"
              className="input-native"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              data-testid="admin-password-input"
              type="password"
              className="input-native"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <button
            data-testid="admin-login-btn"
            disabled={loading}
            className="btn-brand w-full h-11 mt-4 flex items-center justify-center text-[14px]"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-brand">Admin</div>
            <div className="font-display font-bold text-[15px] leading-none mt-0.5">GGES Ventures</div>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-btn"
            className="h-9 px-3 rounded-full border border-neutral-200 flex items-center gap-1.5 text-[12.5px] font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            <LogOut className="w-3.5 h-3.5" /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto p-5">
        {/* Stat cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total users" value={stats?.total_users ?? "—"} icon={UsersIcon} />
          <StatCard label="Paid users" value={stats?.paid_users ?? "—"} icon={CheckCircle2} />
          <StatCard label="Revenue (INR)" value={stats ? `₹${Number(stats.revenue_inr).toLocaleString("en-IN")}` : "—"} icon={Wallet} />
          <StatCard label="Open tickets" value={stats?.tickets_open ?? "—"} icon={MessageSquare} />
        </div>

        {/* Tabs */}
        <div className="mt-6 flex flex-wrap gap-2">
          {["overview", "users", "tickets", "payments", "referrals"].map((k) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`h-9 px-4 rounded-full text-[12.5px] font-semibold ${
                tab === k ? "bg-black text-white" : "bg-white border border-neutral-200 text-neutral-700"
              }`}
            >
              {k[0].toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>

        {/* Panels */}
        {tab === "overview" && (
          <div className="mt-4 p-6 rounded-2xl bg-white border border-neutral-200">
            <div className="font-semibold">Welcome back</div>
            <p className="mt-2 text-[13px] text-neutral-500">
              Use the tabs above to browse Users, Tickets, Payments and Referral events. Verified: {stats?.verified_users ?? 0}, tickets total: {stats?.tickets_total ?? 0}.
            </p>
            <div className="mt-3 flex items-center gap-2 text-[12px] text-neutral-500">
              <TrendingUp className="w-4 h-4 text-emerald-500" /> Referral payout per signup: ₹2,971 (25%)
            </div>
          </div>
        )}

        {tab === "users" && <Table cols={["Name", "Phone", "City", "Country", "Verified", "Paid", "Referrals", "Earnings"]} rows={users.map((u) => [u.name, u.phone, u.city, u.country, u.verified ? "Yes" : "No", u.paid ? "Yes" : "No", u.referral_count ?? 0, `₹${(u.referral_earnings_inr ?? 0).toLocaleString("en-IN")}`])} />}
        {tab === "tickets" && (
          <div className="mt-4 space-y-2">
            {tickets.length === 0 ? <Empty msg="No tickets yet." /> : tickets.map((t) => (
              <div key={t.id} className="p-4 rounded-2xl bg-white border border-neutral-200" data-testid={`ticket-row-${t.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[14px]">{t.subject}</div>
                    <div className="text-[12px] text-neutral-500 mt-0.5">{t.name} · {t.phone} · {t.email || "no email"}</div>
                  </div>
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${t.status === "resolved" ? "bg-emerald-50 text-emerald-600" : "bg-amber-50 text-amber-600"}`}>
                    {t.status.toUpperCase()}
                  </span>
                </div>
                <p className="mt-2 text-[13px] text-neutral-700 whitespace-pre-wrap">{t.message}</p>
                {t.screenshot_base64 && <img src={t.screenshot_base64} alt="ss" className="mt-2 max-h-40 rounded-lg border" />}
                {t.status !== "resolved" && (
                  <button onClick={() => resolve(t.id)} className="mt-3 h-8 px-3 rounded-full bg-black text-white text-[12px] font-semibold">Mark resolved</button>
                )}
              </div>
            ))}
          </div>
        )}
        {tab === "payments" && <Table cols={["Order", "Phone", "Amount (₹)", "Status", "Paid at"]} rows={payments.map((p) => [p.order_id, p.phone, (p.amount / 100).toLocaleString("en-IN"), p.status, p.paid_at?.slice(0, 19) || "—"])} />}
        {tab === "referrals" && <Table cols={["Referrer", "Referred", "Amount (₹)", "Date"]} rows={referrals.map((r) => [r.referrer_id?.slice(0, 8), r.referred_user_id?.slice(0, 8), (r.amount_inr).toLocaleString("en-IN"), r.created_at?.slice(0, 19)])} />}
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-neutral-200">
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] font-semibold uppercase tracking-widest text-neutral-500">{label}</span>
        <Icon className="w-4 h-4 text-brand" />
      </div>
      <div className="mt-2 font-display font-black text-[26px] tracking-tight tabular-nums">{value}</div>
    </div>
  );
}

function Empty({ msg }) {
  return <div className="mt-4 p-6 rounded-2xl bg-white border border-neutral-200 text-center text-[13px] text-neutral-500">{msg}</div>;
}

function Table({ cols, rows }) {
  if (!rows || rows.length === 0) return <Empty msg="No data yet." />;
  return (
    <div className="mt-4 overflow-x-auto rounded-2xl bg-white border border-neutral-200">
      <table className="w-full text-[13px]">
        <thead className="bg-neutral-50 text-[11px] uppercase tracking-widest text-neutral-500">
          <tr>{cols.map((c) => <th key={c} className="text-left px-4 py-2.5 font-semibold">{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-neutral-100">
              {r.map((v, j) => <td key={j} className="px-4 py-3 tabular-nums">{v}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
